import { Position, type InternalNode, type Node } from "@xyflow/react";

// Floating-edge geometry (the standard React Flow recipe, adapted for
// @xyflow/react v12's `measured` + `internals.positionAbsolute`). Instead of
// pinning every edge to a fixed top/bottom handle, we compute where the
// straight line between two node centers crosses each node's border, and pick
// the border side from that point. Result: a return edge enters from the side
// facing its target (left when looping back, right from the other side) rather
// than snaking around to the top — far fewer crossings.

function getNodeIntersection(
  intersectionNode: InternalNode<Node>,
  targetNode: InternalNode<Node>
): { x: number; y: number } {
  const w = (intersectionNode.measured?.width ?? 0) / 2;
  const h = (intersectionNode.measured?.height ?? 0) / 2;
  const ip = intersectionNode.internals.positionAbsolute;
  const tp = targetNode.internals.positionAbsolute;

  const x2 = ip.x + w;
  const y2 = ip.y + h;
  const x1 = tp.x + (targetNode.measured?.width ?? 0) / 2;
  const y1 = tp.y + (targetNode.measured?.height ?? 0) / 2;

  if (w === 0 || h === 0) {
    return { x: x2, y: y2 };
  }

  const xx1 = (x1 - x2) / (2 * w) - (y1 - y2) / (2 * h);
  const yy1 = (x1 - x2) / (2 * w) + (y1 - y2) / (2 * h);
  const a = 1 / (Math.abs(xx1) + Math.abs(yy1) || 1);
  const xx3 = a * xx1;
  const yy3 = a * yy1;
  const x = w * (xx3 + yy3) + x2;
  const y = h * (-xx3 + yy3) + y2;

  return { x, y };
}

function getEdgePosition(
  node: InternalNode<Node>,
  point: { x: number; y: number }
): Position {
  const nx = Math.round(node.internals.positionAbsolute.x);
  const ny = Math.round(node.internals.positionAbsolute.y);
  const w = node.measured?.width ?? 0;
  const h = node.measured?.height ?? 0;
  const px = Math.round(point.x);
  const py = Math.round(point.y);

  if (px <= nx + 1) {
    return Position.Left;
  }
  if (px >= nx + w - 1) {
    return Position.Right;
  }
  if (py <= ny + 1) {
    return Position.Top;
  }
  if (py >= ny + h - 1) {
    return Position.Bottom;
  }
  return Position.Top;
}

export function getEdgeParams(
  source: InternalNode<Node>,
  target: InternalNode<Node>
): {
  sx: number;
  sy: number;
  tx: number;
  ty: number;
  sourcePos: Position;
  targetPos: Position;
} {
  const sourceIntersect = getNodeIntersection(source, target);
  const targetIntersect = getNodeIntersection(target, source);
  return {
    sx: sourceIntersect.x,
    sy: sourceIntersect.y,
    tx: targetIntersect.x,
    ty: targetIntersect.y,
    sourcePos: getEdgePosition(source, sourceIntersect),
    targetPos: getEdgePosition(target, targetIntersect),
  };
}
